import { createSlice } from '@reduxjs/toolkit';
import { UserPermission, User } from 'constants/user.constants';
import { FormInputsTypes } from 'constants/settings.constants';
import { Company } from 'constants/company.constants';
import { getContactCompanies, getCompanyPermissionMapping } from 'services/user.services';
import { globalEndpoints } from 'constants/global.constants';
import _ from 'lodash';
import { logoutUser } from 'services/auth.service';
interface userState {
	userPermission: UserPermission;
	details?: User;
	companies?: Company[];
	loading: boolean;
	loadedComapnies: boolean;
	terms: boolean;
	marketing: boolean;
	permissionByCompany: { [key: number]: UserPermission };
	showPlanSelectionNotification: boolean;
}

const initialState: userState = {
	userPermission: UserPermission.NONE,
	loading: false,
	loadedComapnies: false,
	terms: false,
	marketing: false,
	permissionByCompany: {},
	showPlanSelectionNotification: false,
};

export const userSlice = createSlice({
	name: 'user',
	initialState,
	reducers: {
		login: (state, { payload }) => {
			try {
				const { REACT_APP_ENV } = globalEndpoints;
				if (REACT_APP_ENV === 'development') {
					console.log(payload[0].idTokenClaims);
				}

				state.details = {
					family_name: payload[0]?.idTokenClaims?.family_name,
					given_name: payload[0]?.idTokenClaims?.given_name,
					iat: payload[0]?.idTokenClaims?.iat,
					oid: payload[0].idTokenClaims.oid,
					companyId: Number(localStorage.getItem(payload[0].idTokenClaims.oid)),
					companyName: '',
					companyBankAccount: '',
					companyIdentityNumber: '',
					companyCreationDate: new Date(),
					plan: null,
					planId: null,
          planNextMonthId: null,
          planNextMonth: null,
					companyAccountId: 0,
					role: payload[0]?.idTokenClaims?.extension_permissions || '',
					email: payload[0]?.username,
					phone: payload[0]?.idTokenClaims?.extension_phone || '',
					language: localStorage.getItem('language') || 'en',
				};
				state.permissionByCompany = getCompanyPermissionMapping(payload);
				if (state.details?.companyId === 0) {
					state.details.companyId = Number(Object.keys(state.permissionByCompany)[0]);
				}
				state.userPermission = state.permissionByCompany[state.details?.companyId] || UserPermission.NONE;
			} catch (error) {
				console.log(error);
			}
		},
		setCompanyData: (state, { payload }) => {
			if (state.details) {
				state.details.companyId = payload.id;
				state.details.companyName = payload.name;
				state.details.companyHebrewName = payload.hebrewName;
				state.details.companyIdentityNumber = payload.companyIdentityNumber;
				state.details.plan = payload.plan;
				state.details.planId = payload.planId;
        state.details.planNextMonthId = payload?.planNextMonthId || null;
        state.details.planNextMonth = payload?.planNextMonth || null;
				state.details.companyAccountId = payload.accountId;
				state.details.companyCreationDate = payload.createdAt;
				state.details.isCreditCardUpdated = payload.isCreditCardUpdated;
				//reset balances
				localStorage.setItem(state.details.oid, payload.id);
				state.userPermission = state.permissionByCompany[payload.id] || UserPermission.NONE;
			}
		},

		setCompanyId: (state, { payload }) => {
			if (state.details)
				state.details = {
					...state.details,
					companyId: payload,
				};
		},

		setCompanyPlanId: (state, { payload }) => {
			const findIndex = _.findIndex(state.companies, ['id', state.details?.companyId]);
			if (findIndex > -1 && _.isArray(state.companies)) {
				state.companies[findIndex].planId = payload;
			}
			if (state.details) {
				state.details.planId = payload;
			}
		},

		setCompanyPlan: (state, { payload }) => {
			if (state.details) {
				state.details.plan = payload;
			}
		},


		setCompanyPlanNextMonthId: (state, { payload }) => {
			const findIndex = _.findIndex(state.companies, ['id', state.details?.companyId]);
			if (findIndex > -1 && _.isArray(state.companies)) {
				state.companies[findIndex].planNextMonthId = payload;
			}
			if (state.details) {
				state.details.planNextMonthId = payload;
			}
		},

		setCompanyPlanNextMonth: (state, { payload }) => {
			if (state.details) {
				state.details.planNextMonth = payload;
			}
		},

		setUserData: (state, { payload }) => {
			if (state.details) {
				switch (payload.dataToEdit) {
					case FormInputsTypes.NAME:
						state.details.given_name = `${payload.data.firstName}`;
						state.details.family_name = ` ${payload.data.lastName}`;
						break;
					case FormInputsTypes.EMAIL:
						state.details.email = payload.data.email;
						break;
					case FormInputsTypes.PHONE:
						state.details.phone = payload.data.phone;
						break;
					default:
						state.details.language = payload.data.language;
						break;
				}
			}
		},
		setSelectedCompany: (state, { payload }) => {
			if (state.details) {
				localStorage.setItem(state.details.oid, payload);
				state.details.companyId = payload;
				state.showPlanSelectionNotification = false;
				const obj = _.keyBy(state.companies, 'id');
				setCompanyData(_.get(obj, payload));
			}
		},
		setUserApprovals: (state, { payload }) => {
			const { marketing, terms } = payload;
			state.terms = terms;
			state.marketing = marketing;
		},

		setPermissionByCompany: (state, { payload }) => {
			state.permissionByCompany = payload;
		},
		setShowPlanSelectionNotification: (state, { payload }) => {
			state.showPlanSelectionNotification = payload;
		},
		setUserNames: (state, { payload }) => {
			state.details = { ...state.details, ...payload };
		},
	},
	extraReducers: {
		[getContactCompanies.fulfilled.type]: (state, { payload }) => {
			state.companies = payload;
			state.loadedComapnies = true;
			state.loading = false;
		},
		[getContactCompanies.pending.type]: (state) => {
			state.loading = true;
		},
		[getContactCompanies.rejected.type]: (state) => {
			state.companies = [];
			state.loadedComapnies = true;
			state.loading = false;
		},
	},
});

export const getUserPermission = (state: any) => {
	return state.user.userPermission;
};

export const ifCompaniesLoaded = (state: any) => {
	return state.user.loadedComapnies;
};

export const getUserDetails = (state: any) => {
	return state.user.details;
};

export const getCompanies = (state: any) => {
	return state.user.companies;
};
export const getTerms = (state: any) => {
	return state.user.terms;
};
export const getMarketing = (state: any) => {
	return state.user.marketing;
};
export const getIsDefaultPlan = (state: any) => {
	return state.user.details.plan?.isDefault;
};

export const getCompanyPlanId = (state: any) => {
	return state.user.details.planId;
};

export const getShowPlanSelectedNotification = (state: any) => {
	return state.user.showPlanSelectionNotification;
};

export const {
	login,
	setUserData,
	setCompanyData,
	setCompanyId,
	setSelectedCompany,
	setCompanyPlanId,
	setCompanyPlan,
  setCompanyPlanNextMonthId,
  setCompanyPlanNextMonth,
	setUserApprovals,
	setPermissionByCompany,
	setShowPlanSelectionNotification,
	setUserNames,
} = userSlice.actions;

export default userSlice.reducer;
